import { yupResolver } from '@hookform/resolvers/yup';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import { object, string } from 'yup';
import { useAppSelector } from '../../../hooks';
import { getGclid } from '../../../shared/useGclidTracking';
import { ContactFormStyle } from '../../../styles/components/common/forms';
import Message from '../ui/Message';

// Helper functions to extract GA data
const getGAClientId = (): string | undefined => {
  try {
    // Try to get from _ga cookie (format: GA1.2.XXXXXX.XXXXXX)
    const gaCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_ga='));
    if (gaCookie) {
      const parts = gaCookie.split('.');
      if (parts.length >= 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }
  } catch (error) {
    console.error('Error getting GA client ID:', error);
  }
  return undefined;
};

const getGASessionId = (): string | undefined => {
  try {
    const measurementId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;
    if (!measurementId) return undefined;

    const cookieName = `_ga_${measurementId.replace(/^G-/, '')}`;
    const sessionCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    if (sessionCookie) {
      return decodeURIComponent(sessionCookie.split('=').slice(1).join('='));
    }
  } catch (error) {
    console.error('Error getting GA session ID:', error);
  }
  return undefined;
};

const getLeadTimestamp = (): string => {
  return new Date().toISOString();
};

const DynamicButton = dynamic(() => import('../ui/Button'));
const DynamicInput = dynamic(() => import('../ui/Input'));
const DynamicTextarea = dynamic(() => import('../ui/Textarea'));

export interface IContactFormValues {
  name: string;
  email: string;
  message: string;
  phone?: string;
  website?: string;
  google_client_id?: string;
  google_user_id?: string;
  google_session_id?: string;
  gclid?: string;
  lead_timestamp?: string;
}

export interface IContactFormProps {
  formSubmit: (formData: IContactFormValues) => void;
  setIsButtonDisabled: (isButtonDisabled: boolean) => void;
  isButtonDisabled: boolean;
  darkMode?: boolean;
  labels: {
    name: string;
    email: string;
    message: string;
    submit: string;
    submitSuccessMessage: string;
    submitErrorMessage: string;
  };
  submitResultType: 'success' | 'error' | undefined;
}

const validationSchema = object().shape({
  name: string().trim().required('Name is required'),
  email: string().email('Email is invalid').required('Email is required'),
  message: string().trim().required('Message is required'),
});

const ContactForm = (props: IContactFormProps) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const user = useAppSelector((state) => state.auth.user);

  const initialValues: IContactFormValues = {
    name: '',
    email: '',
    message: '',
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IContactFormValues>({
    resolver: yupResolver(validationSchema),
  });
  const onSubmit = (data: IContactFormValues) => {
    // Enrich data with GA audit fields
    const enrichedData: IContactFormValues = {
      ...data,
      google_client_id: getGAClientId(),
      google_user_id: user?.id?.toString() ?? undefined,
      google_session_id: getGASessionId(),
      gclid: getGclid(),
      lead_timestamp: getLeadTimestamp(),
    };

    props.formSubmit(enrichedData);
    reset(initialValues);
  };
  const onErrors = (errors: any, e: any) => {
    console.log(errors, e);
  };
  return (
    <form
      className={`${ContactFormStyle.contact} ${
        props.darkMode ? ContactFormStyle['dark-mode'] : ''
      }`}
      onSubmit={handleSubmit(onSubmit, onErrors)}
      noValidate>
      <div className={ContactFormStyle['contact-container']}>
        <div className={ContactFormStyle['contact-inputs']}>
          {/* Honey pot field to trap bots */}
          <Controller
            name="website"
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value } }: any) => (
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={value}
                onChange={onChange}
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
                aria-hidden="true"
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value } }: any) => (
              <input
                type="text"
                name="phone"
                tabIndex={-1}
                autoComplete="off"
                value={value}
                onChange={onChange}
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
                aria-hidden="true"
              />
            )}
          />
          {/* Honey pot field to trap bots */}
          <Controller
            name="name"
            control={control}
            defaultValue={''}
            render={({ field: { onChange, value } }: any) => (
              <DynamicInput
                name="name"
                type="text"
                id="name"
                value={value}
                label={props.labels.name}
                error={errors.name?.message}
                onChange={(e: any) => onChange(e.target.value)}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, value } }: any) => (
              <DynamicInput
                name="email"
                type="text"
                id="email"
                value={value}
                label={props.labels.email}
                error={errors.email?.message}
                onChange={(e: any) => onChange(e.target.value)}
              />
            )}
          />
        </div>
        <div className={ContactFormStyle['contact-textarea']}>
          <Controller
            name="message"
            control={control}
            render={({ field: { onChange, value } }: any) => (
              <DynamicTextarea
                name="message"
                type="text"
                id="message"
                styles={{ width: '100%' }}
                value={value}
                label={props.labels.message}
                error={errors.message?.message}
                onChange={(e: any) => onChange(e.target.value)}
              />
            )}
          />
        </div>
        <DynamicButton
          type={'submit'}
          disabled={isButtonDisabled}
          darkMode={props.darkMode}
          color={'tertiary'}>
          {props.labels.submit}
        </DynamicButton>

        {(() => {
          switch (props.submitResultType) {
            case 'success':
              return (
                <>
                  <hr className="separator-thin" />
                  <Message
                    type="success"
                    iconPath={'/assets/images/icons/EmailIcon.svg'}>
                    <ReactMarkdown>
                      {props.labels.submitSuccessMessage}
                    </ReactMarkdown>
                  </Message>
                </>
              );
            case 'error':
              return (
                <>
                  <hr className="separator-thin" />
                  <Message
                    type="danger"
                    iconPath={'/assets/images/icons/AlertIcon.svg'}>
                    <ReactMarkdown>
                      {props.labels.submitErrorMessage}
                    </ReactMarkdown>
                  </Message>
                </>
              );
            default:
              return null;
          }
        })()}
      </div>
    </form>
  );
};

export default ContactForm;
